import { useState, useEffect, useCallback } from 'react';
import { getCurrentUser } from 'aws-amplify/auth';
import { accessControlService } from '../services/accessControlService';

/**
 * Feature names supported by the permissions system
 */
export type FeatureName =
  | 'dashboard'
  | 'upload'
  | 'sheets'
  | 'analyze'
  | 'visualize'
  | 'introspect'
  | 'developer-api'
  | 'ai-connections'
  | 'integrations'
  | 'usage'
  | 'profile'
  | 'stats-dashboard'
  | 'admin'
  | 'flows';

/**
 * Hook to manage and check user permissions
 *
 * Features:
 * - Fetches permissions on mount
 * - Caches permissions to avoid repeated API calls
 * - Provides canAccess function for permission checks
 * - Handles loading and error states
 * - Auto-refreshes when user changes
 *
 * Default behavior: Fails open (returns true) when permissions haven't loaded
 * or when there's an error, for better UX.
 *
 * @returns {Object} Permissions state and utilities
 * @property {Record<string, boolean>} permissions - Map of feature names to enabled/disabled
 * @property {(feature: string) => boolean} canAccess - Check if user can access a feature
 * @property {boolean} isLoading - Whether permissions are being fetched
 * @property {string | null} error - Error message if fetch failed
 * @property {() => Promise<void>} refreshPermissions - Manually refresh permissions
 *
 * @example
 * const { permissions, canAccess, isLoading } = usePermissions();
 *
 * if (!canAccess('developer-api')) {
 *   return <div>Access denied</div>;
 * }
 */
export const usePermissions = () => {
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  /**
   * Fetch permissions from the API
   */
  const fetchPermissions = useCallback(async (currentUserId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await accessControlService.getUserPermissions(currentUserId);

      if (response.success && response.permissions) {
        setPermissions(response.permissions || {});
      } else {
        // If API fails, fail open (allow access) for better UX
        setError(response.error || 'Failed to load permissions');
        setPermissions({});
      }
    } catch (err) {
      // If fetch fails, fail open (allow access) for better UX
      setError(err instanceof Error ? err.message : 'Unknown error');
      setPermissions({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get current user and fetch their permissions
   */
  const loadUserPermissions = useCallback(async () => {
    try {
      const user = await getCurrentUser();
      const currentUserId = user.userId || user.username;

      if (!currentUserId) {
        setError('User ID not found');
        setIsLoading(false);
        return;
      }

      setUserId(currentUserId);
      await fetchPermissions(currentUserId);
    } catch (err) {
      // User not authenticated or error getting user
      setError(err instanceof Error ? err.message : 'Authentication error');
      setIsLoading(false);
    }
  }, [fetchPermissions]);

  /**
   * Load permissions on mount and when user changes
   */
  useEffect(() => {
    loadUserPermissions();
  }, [loadUserPermissions]);

  /**
   * Check if user has access to a specific feature
   *
   * Default behavior: Returns true if permissions haven't loaded yet or if there's
   * an error (fail-open for better UX). Once permissions are loaded, returns the
   * actual permission value.
   *
   * @param feature - The feature name to check
   * @returns true if user has access, false otherwise
   */
  const canAccess = useCallback(
    (feature: string): boolean => {
      // Fail open: If still loading or error, allow access
      if (isLoading || error) {
        return true;
      }

      // If permissions loaded, check actual value
      // Default to true if feature not in permissions (new features are accessible by default)
      return permissions[feature] !== false;
    },
    [permissions, isLoading, error]
  );

  /**
   * Manually refresh permissions from the API
   */
  const refreshPermissions = useCallback(async () => {
    if (userId) {
      await fetchPermissions(userId);
    } else {
      await loadUserPermissions();
    }
  }, [userId, fetchPermissions, loadUserPermissions]);

  return {
    permissions,
    canAccess,
    isLoading,
    error,
    refreshPermissions,
  };
};
